import api from "@/lib/axiosConfig";
import { useQuery } from "@tanstack/react-query";

/**
 * Présence de champs : quels réglages d'impression ont une valeur dans la
 * sélection courante.
 *
 * Le calcul est fait par l'API (`POST back/api/pdf/field-presence`) et non ici,
 * pour deux raisons : le drawer multi ne dispose que des identifiants, et la
 * fiche unique charge l'œuvre sans ses relations (`useGetArtworkById(id, false)`),
 * donc ni le suivi financier ni les enregistrements liés ne sont décidables
 * localement.
 */

export type PresenceTable = "artwork" | "photography" | "multipleprint";

export type FieldPresenceResponse = {
  table: PresenceTable;
  requested: number;
  matched: number;
  /** Calcul non effectué : tout est à `true`. */
  degraded: boolean;
  present: Record<string, boolean>;
};

async function fetchFieldPresence(
  table: PresenceTable,
  ids: string[],
): Promise<FieldPresenceResponse> {
  const { data } = await api.post<FieldPresenceResponse>(
    "back/api/pdf/field-presence",
    { table, ids },
  );
  return data;
}

/**
 * Clé de cache stable pour une sélection.
 *
 * Triée et dédoublonnée : sélectionner A puis B, ou B puis A, décrit la même
 * requête et doit toucher la même entrée de cache.
 */
export function selectionKey(ids: readonly string[]): string {
  return [...new Set(ids)].sort().join(",");
}

/**
 * Le cache vit ici, pas côté serveur.
 *
 * Un cache serveur aurait le mauvais mode de défaillance : une entrée périmée
 * ferait disparaître la case d'un champ qu'on vient de remplir. React-query le
 * fait au plus près de l'utilisateur, avec une fenêtre courte, et couvre le
 * seul cas réellement répété — rouvrir le même drawer.
 */
export function useFieldPresence(
  table: PresenceTable,
  ids: string[],
  enabled: boolean,
) {
  const key = selectionKey(ids);
  return useQuery({
    queryKey: ["field-presence", table, key],
    queryFn: () => fetchFieldPresence(table, ids),
    enabled: enabled && ids.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Une seule tentative : le drawer affiche tout pendant ce temps, insister
    // ne ferait que retarder un affichage déjà correct.
    retry: 1,
  });
}
